import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/auth';
import { usePendingInvites, useAcceptInvite, useDeclineInvite } from '../hooks/useTeamSettings';
import { GoogleLogo } from './shared/Icons';

interface InvitePageProps {
  authenticated: boolean;
}

export function InvitePage({ authenticated }: InvitePageProps) {
  if (!authenticated) return <UnauthenticatedView />;
  return <AuthenticatedView />;
}

function UnauthenticatedView() {
  const handleSignIn = () => {
    const redirectTo = window.location.origin + window.location.pathname;
    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
  };

  return (
    <PageShell>
      <h1 style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
        You've been invited to a team
      </h1>
      <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.5 }}>
        Sign in to view and accept this invitation.
      </p>
      <button onClick={handleSignIn} style={{
        display: 'inline-flex', alignItems: 'center', gap: 10,
        fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
        padding: '10px 12px 10px 12px', borderRadius: 6, cursor: 'pointer',
        background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 600,
      }}>
        <span style={{
          display: 'grid', placeItems: 'center', width: 24, height: 24,
          borderRadius: 4, background: '#fff', flexShrink: 0,
        }}>
          <GoogleLogo size={15} />
        </span>
        <span style={{ paddingRight: 16 }}>SIGN IN WITH GOOGLE</span>
      </button>
      <p style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--text3)', marginTop: 20 }}>
        Don't have an account? Signing in will create one automatically.
      </p>
    </PageShell>
  );
}

function AuthenticatedView() {
  const { id: inviteId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: pendingData, isLoading } = usePendingInvites();
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const [confirmJoin, setConfirmJoin] = useState(false);

  const invite = pendingData?.invites.find(inv => inv.id === inviteId);
  const currentTeam = pendingData?.current_team;

  const leaveMsg = currentTeam
    ? currentTeam.member_count <= 1
      ? `Your current team "${currentTeam.name}" will be deleted (you're the only member).`
      : `You'll leave "${currentTeam.name}" (${currentTeam.member_count - 1} member${currentTeam.member_count - 1 !== 1 ? 's' : ''} remaining).`
    : null;

  if (isLoading) {
    return (
      <PageShell>
        <span style={{ color: 'var(--text2)', fontSize: 14 }}>Loading invite...</span>
      </PageShell>
    );
  }

  if (!invite) {
    const handleSwitchAccount = async () => {
      await supabase.auth.signOut();
      window.location.reload();
    };

    return (
      <PageShell>
        <h1 style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
          Invite not found
        </h1>
        <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--text3)', margin: '0 0 28px', lineHeight: 1.5 }}>
          This invite may have expired, already been accepted, or is for a different account.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => navigate('/briefing')} style={{
            fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
            padding: '10px 24px', borderRadius: 6, cursor: 'pointer',
            background: 'none', color: 'var(--text2)', border: '1px solid var(--border)',
          }}>
            GO TO DASHBOARD
          </button>
          <button onClick={handleSwitchAccount} style={{
            fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
            padding: '10px 24px', borderRadius: 6, cursor: 'pointer',
            background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 600,
          }}>
            SWITCH ACCOUNT
          </button>
        </div>
      </PageShell>
    );
  }

  // useAcceptInvite now invalidates the whole query cache (the user's team changed, so every
  // team-scoped query is stale), which is what the old `location.assign('/#/team-today')` was
  // really buying — at the cost of a document-level navigation to a retired address that then
  // redirected again. Route straight to the live briefing.
  const handleAccept = () => {
    accept.mutate({ teamId: invite.team_id, inviteId: invite.id }, {
      onSuccess: () => navigate('/briefing'),
    });
  };

  const handleDecline = () => {
    decline.mutate({ teamId: invite.team_id, inviteId: invite.id }, {
      onSuccess: () => navigate('/briefing'),
    });
  };

  return (
    <PageShell>
      <div style={{
        width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)',
        boxShadow: '0 0 12px rgba(131,119,209,0.5)', marginBottom: 20,
      }} />
      <h1 style={{ fontFamily: 'var(--display)', fontSize: 24, fontWeight: 600, color: 'var(--text)', margin: '0 0 8px' }}>
        Join {invite.team_name}
      </h1>
      <p style={{ fontFamily: 'var(--sans)', fontSize: 15, color: 'var(--text3)', margin: '0 0 24px', lineHeight: 1.5 }}>
        {invite.invited_by_email} invited you to their team on Terum.
        Join to share conversation context with your teammates.
      </p>

      {confirmJoin && leaveMsg && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 6,
          background: 'rgba(255,184,74,0.08)', border: '1px solid rgba(255,184,74,0.25)',
          fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--amber)', lineHeight: 1.5,
        }}>
          {leaveMsg}
        </div>
      )}

      {accept.isError && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', borderRadius: 6,
          background: 'rgba(255,107,138,0.08)', border: '1px solid rgba(255,107,138,0.25)',
          fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--rose)',
        }}>
          Failed to join team. Try again.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        {confirmJoin ? (
          <>
            <button onClick={() => setConfirmJoin(false)} style={{
              fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
              padding: '10px 24px', borderRadius: 6, cursor: 'pointer',
              background: 'none', color: 'var(--text2)', border: '1px solid var(--border)',
            }}>CANCEL</button>
            <button onClick={handleAccept} disabled={accept.isPending} style={{
              fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
              padding: '10px 28px', borderRadius: 6, cursor: 'pointer',
              background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 600,
              opacity: accept.isPending ? 0.6 : 1,
            }}>
              {accept.isPending ? 'JOINING...' : 'CONFIRM JOIN'}
            </button>
          </>
        ) : (
          <>
            <button onClick={handleDecline} disabled={decline.isPending} style={{
              fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
              padding: '10px 24px', borderRadius: 6, cursor: 'pointer',
              background: 'none', color: 'var(--text2)', border: '1px solid var(--border)',
              opacity: decline.isPending ? 0.6 : 1,
            }}>
              {decline.isPending ? 'DECLINING...' : 'DECLINE'}
            </button>
            <button onClick={() => currentTeam ? setConfirmJoin(true) : handleAccept()} style={{
              fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' as const,
              padding: '10px 28px', borderRadius: 6, cursor: 'pointer',
              background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 600,
            }}>
              JOIN TEAM
            </button>
          </>
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: 32,
    }}>
      <div style={{
        maxWidth: 440, width: '100%', textAlign: 'center',
        padding: '48px 32px', borderRadius: 12,
        background: 'var(--bg1)', border: '1px solid var(--border)',
      }}>
        <div style={{ marginBottom: 24 }}>
          <span style={{ fontWeight: 700, fontSize: 18, letterSpacing: '0.08em', color: 'var(--text)' }}>TERUM</span>
        </div>
        {children}
      </div>
    </div>
  );
}
